import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { AdminProgressReviewRepository } from './domain'

import { AxiosErrorHandler, SupernovaPagedBody, SupernovaPagedResponse } from '../../foundation'
import {
  AdminProgressReviewBlurRequest,
  AdminProgressReviewListCriteria,
  AdminProgressReviewListItem,
  AdminProgressReviewMemoRequest,
  AdminProgressReviewRewardStatusRequest,
  AdminProgressReviewStatusRequest,
  AdminReviewRewardListCriteria,
  AdminReviewRewardListItem,
  AdminReviewRewardSortCriteria,
  AdminReviewVerificationStatusRequest,
} from '../../contracts/review'
import { AdminExecuteRewardPointRequest } from '../../contracts/review/AdminRewardPointRequest'

export class AdminApiProgressReview implements AdminProgressReviewRepository {
  private errorHandler: AxiosErrorHandler

  constructor(private axios: AxiosInstance) {
    this.errorHandler = new AxiosErrorHandler()
  }

  getProgressReviewList = async (
    criteria: AdminProgressReviewListCriteria,
  ): Promise<SupernovaPagedBody<AdminProgressReviewListItem[]>> => {
    const config: AxiosRequestConfig<AdminProgressReviewListCriteria> = {
      url: '/review/query/admin/get-progress-review-list-views/process',
      method: 'post',
      data: criteria,
    }
    try {
      const { data } = await this.axios.request<SupernovaPagedResponse<AdminProgressReviewListItem[]>>(config)
      return data.body
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  blurProgressReview = async (command: AdminProgressReviewBlurRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminProgressReviewBlurRequest> = {
      url: '/review/command/admin/blur-progress-photos/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  modifyProgressReviewStatus = async (command: AdminProgressReviewStatusRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminProgressReviewStatusRequest> = {
      url: '/review/command/admin/change-progress-ongoing-status/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getRewardList = async (
    criteria: AdminReviewRewardListCriteria,
  ): Promise<SupernovaPagedBody<AdminReviewRewardListItem[]>> => {
    const config: AxiosRequestConfig<AdminReviewRewardListCriteria> = {
      url: '/review/query/admin/get-point-reward-process-list-views/process',
      method: 'post',
      data: {
        ...criteria,
        ...this.getReviewRewardsSortCriteria(criteria),
      },
    }
    try {
      const { data } = await this.axios.request<SupernovaPagedResponse<AdminReviewRewardListItem[]>>(config)
      return data.body
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  private getReviewRewardsSortCriteria = (criteria: AdminReviewRewardListCriteria): AdminReviewRewardSortCriteria => {
    return {
      sort: criteria.sort?.orders?.length
        ? criteria.sort
        : {
            orders: [{ fieldName: 'progressCompletedAtUtc', direction: 'DESC' }],
          },
    }
  }

  executeRewardPoint = async (command: AdminExecuteRewardPointRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminExecuteRewardPointRequest> = {
      url: '/review/command/admin/reward-point/execute',
      method: 'post',
      data: command,
    }

    try {
      await this.axios.request(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  modifyRewardStatus = async (command: AdminProgressReviewRewardStatusRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminProgressReviewRewardStatusRequest> = {
      url: '/review/command/admin/change-point-reward-status/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  modifyRewardVerificationStatus = async (command: AdminReviewVerificationStatusRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminReviewVerificationStatusRequest> = {
      url: '/review/command/admin/change-point-reward-verification-status/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  modifyMemo = async (command: AdminProgressReviewMemoRequest): Promise<void> => {
    const config: AxiosRequestConfig<AdminProgressReviewMemoRequest> = {
      url: '/review/command/admin/post-point-reward-process-memo/execute',
      method: 'post',
      data: command,
    }
    try {
      await this.axios.request(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getRewardListCSV = async (criteria: AdminReviewRewardListCriteria, pages: number[]): Promise<Blob> => {
    const config: AxiosRequestConfig<AdminReviewRewardListCriteria & { pages: number[] }> = {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/csv',
      },
      url: '/review/query/admin/download-point-reward-process-list-as-csv/process',
      method: 'post',
      responseType: 'blob',
      data: {
        ...criteria,
        sort: {
          orders: [
            {
              fieldName: 'lastEditedAtUtc',
              direction: 'DESC',
            },
          ],
        },
        pages,
      },
    }
    try {
      const { data } = await this.axios.request<Blob>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }
}
